import { useState, useEffect } from "react";
import { Head, Link } from "@inertiajs/react";
import Layout from "../Layout";
import { CodeSnippetWithCopy } from "@/components/ui/CodeSnippetWithCopy";
import { FAQ, type FAQItem } from "@/components/marketing/FAQ";
import { ChevronRight, Server, Layers, Container, GitPullRequest, HardDrive, Cpu } from "lucide-react";
import { SiTerraform, SiGithub } from "@icons-pack/react-simple-icons";
import { SlackIcon } from "@/components/icons/SlackIcon";
import { AwsIcon } from "@/components/icons/AwsIcon";

const TECH_ARTICLE_JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "TechArticle",
  headline: "Infralyst Documentation",
  description:
    "Everything you need to connect AWS, GitHub, and Terraform to Infralyst and start optimizing your infrastructure.",
  url: "https://infralyst.io/docs",
  publisher: {
    "@type": "Organization",
    name: "Infralyst",
    url: "https://infralyst.io",
  },
});

const AWS_MODULE_HCL = `module "infralyst_readonly_integration" {
  source  = "infralyst/readonly-integration/aws"
  version = "~> 1.0"

  external_id = "<your_workspace_external_id>"
}

output "infralyst_role_arn" {
  value       = module.infralyst_readonly_integration.role_arn
  description = "Paste this Role ARN into Infralyst"
}`;

const S3_BACKEND_HCL = `# Provider alias for the region where your state bucket lives.
# If you already have this provider defined, you can skip this block.
provider "aws" {
  alias  = "infralyst_state_region"
  region = "<your-bucket-region>"
}

resource "aws_s3_bucket_policy" "infralyst_state_access" {
  provider = aws.infralyst_state_region
  bucket   = "<your-state-bucket>"

  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid    = "InfralystReadAccess"
      Effect = "Allow"
      Principal = {
        AWS = "arn:aws:iam::<infralyst_account_id>:role/infralyst-readonly"
      }
      Action = [
        "s3:GetObject",
        "s3:ListBucket"
      ]
      Resource = [
        "arn:aws:s3:::<your-state-bucket>",
        "arn:aws:s3:::<your-state-bucket>/<your-state-key>"
      ]
    }]
  })
}`;

const EC2_PERMISSIONS = [
  "ec2:DescribeInstances",
  "ec2:DescribeInstanceTypes",
  "ec2:DescribeLaunchTemplates",
  "ec2:DescribeLaunchTemplateVersions",
  "ec2:DescribeVolumes",
  "ec2:DescribeTags",
] as const;

const AUTOSCALING_PERMISSIONS = [
  "autoscaling:DescribeAutoScalingGroups",
  "autoscaling:DescribeAutoScalingInstances",
  "autoscaling:DescribeScalingActivities",
] as const;

const ECS_PERMISSIONS = [
  "ecs:ListClusters",
  "ecs:ListServices",
  "ecs:DescribeServices",
  "ecs:DescribeTaskDefinition",
] as const;

const CLOUDWATCH_PERMISSIONS = [
  "cloudwatch:GetMetricData",
  "cloudwatch:GetMetricStatistics",
] as const;

const TOC_SECTIONS = [
  { id: "connect-aws", label: "Connect AWS" },
  { id: "add-github", label: "Add GitHub" },
  { id: "map-terraform-state", label: "Map Terraform state" },
  { id: "how-prs-are-generated", label: "How PRs are generated" },
  { id: "add-slack", label: "Add Slack" },
  { id: "supported-resources", label: "Supported resources" },
  { id: "faq", label: "FAQ" },
] as const;

const SETUP_STEPS = [
  { n: 1, label: "Connect AWS", href: "#connect-aws" },
  { n: 2, label: "Add GitHub", href: "#add-github" },
  { n: 3, label: "Map Terraform state", href: "#map-terraform-state" },
  { n: 4, label: "Add Slack", href: "#add-slack" },
] as const;

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "How long until I see recommendations?",
    answer:
      "Infralyst pulls up to a year of historical CloudWatch data when you first connect an AWS account. Once the initial fetch and analysis completes, Infralyst generates up to 5 recommendations for your highest-savings opportunities first. After that, one new recommendation is generated per day. Each individual resource needs at least 30 days of data at 95% uptime before it's eligible.",
  },
  {
    question: "Does Infralyst use AI to decide what to recommend?",
    answer:
      "No. Infralyst recommendations are deterministic. The analysis checks CPU and memory P99.5 thresholds, data quality gates, and weekly seasonality patterns. No machine learning is involved in the decision. AI is only used in the final step: an agent reads your Terraform repo and writes the code change for the pull request.",
  },
  {
    question: "Do I need GitHub and Terraform state connected to see recommendations?",
    answer:
      "No. Recommendations are generated from AWS CloudWatch data alone. GitHub and Terraform state are only required when you want to generate a pull request. You can connect AWS first, review recommendations, and add the other integrations later.",
  },
  {
    question: "What counts as a PR on the Free plan?",
    answer:
      "Each time you generate a Terraform pull request from a recommendation, that uses one of your 3 free PRs. Viewing recommendations doesn't use a PR. Upgrading to Pro unlocks unlimited PR generation.",
  },
  {
    question: "Can I undo a change Infralyst made?",
    answer:
      "Yes. Every change is a standard Terraform pull request in your repo. If you've already merged and applied a PR and want to roll back, revert the PR in GitHub and run terraform apply again. Infralyst never merges or applies changes on your behalf.",
  },
  {
    question: "What happens if the AI agent makes a mistake in the Terraform PR?",
    answer:
      "The agent validates syntax and reviews for edge cases like shared variables and lifecycle blocks, but every PR goes through your normal GitHub review process. Infralyst never merges or applies changes. If something looks wrong, close the PR. You can also revert a merged PR and run terraform apply to roll back.",
  },
  {
    question: "Can I use Infralyst with multiple AWS accounts and Terraform repos?",
    answer:
      "Yes. You can connect multiple AWS accounts to a single workspace and map each to one or more Terraform state files across different GitHub repos. One repo managing multiple accounts, or multiple repos for one account, both work.",
  },
  {
    question: "Does Infralyst store my Terraform state or AWS credentials?",
    answer:
      "No. Infralyst never stores AWS credentials. Access is through a read-only IAM role with an External ID unique to your workspace. Terraform state is loaded on demand when generating a PR, read once for resource mappings, and discarded immediately.",
  },
  {
    question: "Does Infralyst merge pull requests or apply changes automatically?",
    answer:
      "No. Infralyst never merges PRs or modifies your AWS infrastructure. Every recommendation is a suggestion you can accept or dismiss. When you generate a PR, your team reviews the code diff and utilization data, then merges on your schedule. If something doesn't look right, you close the PR and nothing changes.",
  },
  {
    question: "How does Infralyst handle downtime risk for EC2 rightsizing?",
    answer:
      "Infralyst classifies each resource by deployment type. Standalone EC2 instances require a stop/start, so the PR includes a downtime warning. Multi-instance Auto Scaling Groups use instance refresh for zero downtime rollouts. Single-instance ASGs and single-task Fargate services also get downtime warnings.",
  },
];

function Perm({ name }: { name: string }): React.ReactElement {
  return <code className="text-xs bg-surface px-1 py-0.5 rounded">{name}</code>;
}

function SectionIcon({
  icon: Icon,
  className,
  iconClassName = "size-5",
}: {
  icon: React.ElementType;
  className: string;
  iconClassName?: string;
}): React.ReactElement {
  return (
    <div
      className={`size-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${className}`}
    >
      <Icon className={`${iconClassName} text-white`} />
    </div>
  );
}

export default function Show(): React.ReactElement {
  const [activeSection, setActiveSection] = useState<string>("connect-aws");
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0% -70% 0%", threshold: 0 },
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout
      title="Docs"
      description="Everything you need to connect AWS, GitHub, and Terraform to Infralyst and start optimizing your infrastructure."
      showGradient={false}
    >
      <Head>
        <script type="application/ld+json">{TECH_ARTICLE_JSON_LD}</script>
      </Head>
      <div className="max-w-5xl mx-auto px-4 pt-40 pb-24 lg:flex lg:gap-12">
        {/* Sticky sidebar */}
        <aside className="hidden lg:block w-52 flex-shrink-0">
          <div className="sticky top-28">
            <nav className="space-y-0.5">
              {TOC_SECTIONS.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`block px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    activeSection === id
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-surface-foreground/70 hover:text-foreground hover:bg-surface/50"
                  }`}
                >
                  {label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Hero */}
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-primary">Infralyst</span> Docs
          </h1>
          <p className="text-surface-foreground/80 leading-relaxed mb-4">
            Infralyst analyzes your AWS infrastructure and generates Terraform
            pull requests to find savings.{" "}
            <Link href="/signup" className="text-primary hover:underline">
              Sign up
            </Link>{" "}
            to create a workspace for your team. Every workspace starts on the
            Free plan with 3 free PRs.
          </p>
          <p className="text-surface-foreground/80 leading-relaxed mb-8">
            Once your AWS account is connected, Infralyst pulls up to a year of
            historical usage data so savings recommendations can start appearing once
            the initial fetch and analysis is complete. You don't need GitHub or
            Terraform state connected to see recommendations. Those integrations
            are only required when you want to generate a pull request.
          </p>

          {/* Step flow */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-10 text-sm">
            {SETUP_STEPS.map(({ n, label, href }, i) => (
              <span key={n} className="flex items-center gap-3">
                <a
                  href={href}
                  className="flex items-center gap-2 text-surface-foreground/70 hover:text-foreground transition-colors"
                >
                  <span className="size-6 rounded-full bg-surface border flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {n}
                  </span>
                  {label}
                </a>
                {i < SETUP_STEPS.length - 1 && (
                  <ChevronRight className="size-3 text-surface-foreground/40 flex-shrink-0" />
                )}
              </span>
            ))}
          </div>

          <hr className="border-border" />

          {/* Connect AWS */}
          <section id="connect-aws" className="scroll-mt-24 py-12">
            <div className="flex items-center gap-4 mb-4">
              <SectionIcon
                icon={AwsIcon}
                className="bg-[#FF9900]"
                iconClassName="size-7"
              />
              <h2 className="text-3xl font-bold">Connect AWS</h2>
            </div>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Infralyst connects to your AWS accounts through a read-only IAM
              role provisioned by a Terraform module. We never get write access
              to your infrastructure and we never store AWS credentials.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">
              What the Terraform module creates
            </h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              The module creates a single IAM role (
              <code className="text-xs bg-surface px-1 py-0.5 rounded">
                infralyst-readonly
              </code>
              ) in your account with a trust policy that allows Infralyst's AWS
              account to assume it via STS. An External ID, unique to your
              workspace, is required for every assume-role call to prevent the{" "}
              <a
                href="https://docs.aws.amazon.com/IAM/latest/UserGuide/confused-deputy.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                confused deputy problem
              </a>
              . No long-lived access keys are created.
            </p>

            {/* Collapsible permissions */}
            <div className="mb-6">
              <button
                onClick={() => setPermissionsOpen(!permissionsOpen)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-colors ${
                  permissionsOpen
                    ? "bg-surface/80"
                    : "bg-surface/50 hover:bg-surface/70"
                }`}
                aria-expanded={permissionsOpen}
              >
                <div>
                  <p className="font-semibold text-sm">IAM permissions</p>
                  <p className="text-xs text-surface-foreground/60 mt-0.5">
                    EC2 · Auto Scaling · ECS · CloudWatch, all read-only
                  </p>
                </div>
                <ChevronRight
                  className={`size-4 text-surface-foreground/60 flex-shrink-0 transition-transform duration-200 ${
                    permissionsOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              {permissionsOpen && (
                <div className="mt-1 p-5 rounded-xl border bg-background/50 space-y-5">
                  <div>
                    <p className="font-semibold text-sm mb-2">EC2</p>
                    <ul className="space-y-1.5">
                      {EC2_PERMISSIONS.map((p) => (
                        <li key={p}>
                          <Perm name={p} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-2">Auto Scaling</p>
                    <ul className="space-y-1.5">
                      {AUTOSCALING_PERMISSIONS.map((p) => (
                        <li key={p}>
                          <Perm name={p} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-2">ECS</p>
                    <ul className="space-y-1.5">
                      {ECS_PERMISSIONS.map((p) => (
                        <li key={p}>
                          <Perm name={p} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-2">CloudWatch</p>
                    <ul className="space-y-1.5">
                      {CLOUDWATCH_PERMISSIONS.map((p) => (
                        <li key={p}>
                          <Perm name={p} />
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-surface-foreground/60 leading-relaxed pt-1 border-t border-border">
                    All permissions use{" "}
                    <code className="text-xs bg-surface px-1 py-0.5 rounded">
                      "Resource": "*"
                    </code>{" "}
                    because these are describe/list operations that{" "}
                    <a
                      href="https://docs.aws.amazon.com/service-authorization/latest/reference/list_amazonec2.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      don't support resource-level permissions
                    </a>
                    . You can review the exact policy JSON via the{" "}
                    <code className="text-xs bg-surface px-1 py-0.5 rounded">
                      policy_json
                    </code>{" "}
                    output or in the AWS Console.
                  </p>
                </div>
              )}
            </div>

            <h3 className="text-xl font-bold mb-3 mt-8">
              What data we collect
            </h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              For each supported resource type, Infralyst collects CPU
              utilization and memory utilization from CloudWatch, along with
              resource configuration metadata (instance types, launch templates,
              task definitions, Auto Scaling Group settings, and ECS service
              definitions). On first connection, we fetch up to a year of
              historical data. After that, we poll every 4 hours to keep the
              usage history current.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              We use this data to build a usage profile for each resource and
              generate recommendations. We don't access application
              data, secrets, or anything outside the permissions listed above.
              Terraform state is loaded on demand when generating a PR, read
              once, and discarded. See the{" "}
              <a
                href="#supported-resources"
                className="text-primary hover:underline"
              >
                Supported resources
              </a>{" "}
              section for how each type is analyzed and what thresholds trigger
              a recommendation.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">
              When recommendations appear
            </h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              After the initial data fetch and analysis completes, Infralyst
              generates up to 5 recommendations for your highest-savings
              opportunities first. After that, one new recommendation is
              generated per day as more resources are analyzed.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Each resource needs a minimum of 30 days of data at 95% uptime
              before it's eligible for a recommendation. This can vary by
              resource type. See the individual resource pages under{" "}
              <a
                href="#supported-resources"
                className="text-primary hover:underline"
              >
                Supported resources
              </a>{" "}
              for specifics.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">Setup</h3>
            <ol className="list-decimal list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>Add an AWS account in your workspace.</li>
              <li>
                Follow the in-app steps to apply the Infralyst Terraform module
                in one of your infra repos:
              </li>
            </ol>
            <CodeSnippetWithCopy
              code={AWS_MODULE_HCL}
              label="Terraform module"
              ariaLabel="Copy AWS module HCL"
              language="hcl"
              showSyntaxHighlighting={true}
            />
            <ol
              className="list-decimal list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-6"
              start={3}
            >
              <li>
                Run{" "}
                <code className="text-xs bg-surface px-1 py-0.5 rounded">
                  terraform init
                </code>{" "}
                and{" "}
                <code className="text-xs bg-surface px-1 py-0.5 rounded">
                  terraform apply
                </code>
                .
              </li>
              <li>
                Copy the{" "}
                <code className="text-xs bg-surface px-1 py-0.5 rounded">
                  infralyst_role_arn
                </code>{" "}
                output and paste it into Infralyst.
              </li>
              <li>
                Click <strong>Test connection</strong> to verify.
              </li>
            </ol>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              The app provides your External ID and walks you through each step.
            </p>
            <p className="mb-6">
              <a
                href="https://registry.terraform.io/modules/infralyst/readonly-integration/aws/latest"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                View the Terraform module on the Terraform Registry →
              </a>
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">Disconnecting</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Disconnect the AWS account from your workspace settings in
              Infralyst. This stops all data collection and analysis
              immediately.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              To fully remove access on your end, remove the Terraform module
              block from your configuration and run{" "}
              <code className="text-xs bg-surface px-1 py-0.5 rounded">
                terraform apply
              </code>{" "}
              to destroy the IAM role.
            </p>
          </section>

          <hr className="border-border" />

          {/* Add GitHub */}
          <section id="add-github" className="scroll-mt-24 py-12">
            <div className="flex items-center gap-4 mb-4">
              <SectionIcon
                icon={SiGithub}
                className="bg-neutral-900 dark:bg-neutral-700"
              />
              <h2 className="text-3xl font-bold">Add GitHub</h2>
            </div>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Infralyst opens pull requests in your repositories through a
              GitHub App. We never push directly to your main branch and we
              never merge changes on your behalf. Every change goes through your
              normal review process.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">App permissions</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              The Infralyst GitHub App requests:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>
                <strong>Read access</strong> to repository metadata.
              </li>
              <li>
                <strong>Read and write access</strong> to code, pull requests,
                and repository hooks.
              </li>
            </ul>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Write access to code and pull requests is what allows Infralyst to
              open PRs with Terraform changes. Repository hooks let us stay in
              sync when relevant files change.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">Choosing repos</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              During installation you select which repositories the app can
              access. Choose only the repos that contain your Terraform
              infrastructure code. The app cannot see or interact with any
              repository you haven't explicitly selected.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              You can change your repo selection at any time from the GitHub App
              settings.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">
              What the app doesn't do
            </h3>
            <ul className="list-disc list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>Push to main or any protected branch.</li>
              <li>Merge or approve pull requests.</li>
              <li>Access repositories you haven't selected.</li>
              <li>
                Read or modify anything outside of Terraform files relevant to a
                recommendation.
              </li>
            </ul>

            <h3 className="text-xl font-bold mb-3 mt-8">Setup</h3>
            <ol className="list-decimal list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>Install the Infralyst GitHub App from your workspace.</li>
              <li>
                Choose your GitHub organization and select the repositories that
                contain your Terraform code.
              </li>
              <li>Add those repos to your workspace.</li>
            </ol>
          </section>

          <hr className="border-border" />

          {/* Map Terraform state */}
          <section id="map-terraform-state" className="scroll-mt-24 py-12">
            <div className="flex items-center gap-4 mb-4">
              <SectionIcon icon={SiTerraform} className="bg-[#844FBA]" />
              <h2 className="text-3xl font-bold">Map Terraform state</h2>
            </div>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Terraform state is where everything connects. A state mapping
              tells Infralyst which state file belongs to which AWS account and
              lives in which GitHub repo. This is how recommendations turn into
              pull requests that land in the right place.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">Why we need this</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Your Terraform state file is the link between a running AWS
              resource and the{" "}
              <code className="text-xs bg-surface px-1 py-0.5 rounded">
                .tf
              </code>{" "}
              code that defines it. Infralyst uses it to match a resource
              identified through CloudWatch metrics to the exact file, resource
              block, and attribute that needs to change in a pull request.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              We don't store a copy of your state. We load it on demand when
              generating a PR, read the resource mappings we need, and discard
              it.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">How mapping works</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              For each AWS account, you add one or more Terraform states. Each
              state connects:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>
                An <strong>AWS account</strong> (the source of metrics and
                recommendations)
              </li>
              <li>
                A <strong>backend</strong> (where the state file lives, either
                GitHub or S3)
              </li>
              <li>
                A <strong>GitHub repo</strong> (the repo that contains the
                Terraform code for resources in that state)
              </li>
            </ul>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              This supports any infrastructure layout. One repo managing
              multiple AWS accounts, multiple repos for a single account, or any
              other combination. Just add the state mappings that match your
              setup.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">S3 backend</h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              If your Terraform state is stored in S3, Infralyst needs read
              access to the bucket. The app generates the exact bucket policy
              for you during setup:
            </p>
            <CodeSnippetWithCopy
              code={S3_BACKEND_HCL}
              label="S3 bucket policy"
              ariaLabel="Copy S3 backend policy HCL"
              language="hcl"
              showSyntaxHighlighting={true}
            />
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              The app fills in the correct values for your bucket, state path,
              and Infralyst's account ID. Apply this alongside the read-only
              integration module.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">Setup</h3>
            <ol className="list-decimal list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>
                In your workspace, go to the AWS account you want to configure.
              </li>
              <li>Add a Terraform state.</li>
              <li>Choose the backend (GitHub or S3).</li>
              <li>If using S3, apply the generated bucket policy.</li>
              <li>
                Select the GitHub repo that owns the Terraform code for that
                state.
              </li>
              <li>Repeat for each state file associated with that account.</li>
            </ol>
          </section>

          <hr className="border-border" />

          {/* How PRs are generated */}
          <section id="how-prs-are-generated" className="scroll-mt-24 py-12">
            <div className="flex items-center gap-4 mb-4">
              <SectionIcon icon={GitPullRequest} className="bg-primary" />
              <h2 className="text-3xl font-bold">How PRs are generated</h2>
            </div>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              When you click "Generate PR," an AI agent reads your Terraform
              repository and writes the code change. The agent locates the
              relevant resource definitions, applies the change, validates the
              syntax, and reviews the result for edge cases. For example, it
              detects shared variables that could affect other resources,
              lifecycle blocks that would prevent the change from taking effect,
              and launch templates shared across multiple Auto Scaling Groups.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">
              Warnings in pull requests
            </h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              PRs include warnings when a change could affect availability or
              performance:
            </p>
            <ul className="list-disc list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>
                <strong>Downtime required</strong> for standalone EC2 instances
                (stop/start needed), single-instance Auto Scaling Groups, and
                single-task Fargate services. Multi-instance ASGs use instance
                refresh for zero downtime rollouts. Multi-task Fargate services
                use rolling deployments.
              </li>
              <li>
                <strong>Throughput and bandwidth</strong> warnings when a
                smaller instance type or task size could reduce network or EBS
                throughput ceilings.
              </li>
            </ul>

            <h3 className="text-xl font-bold mb-3 mt-8">
              What the AI agent doesn't do
            </h3>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              The AI agent only generates the Terraform code change. It never
              pushes to your main branch, never merges pull requests, and never
              applies anything to your AWS infrastructure. Every change goes
              through your normal GitHub review and merge process.
            </p>
          </section>

          <hr className="border-border" />

          {/* Add Slack */}
          <section id="add-slack" className="scroll-mt-24 py-12">
            <div className="flex items-center gap-4 mb-4">
              <SectionIcon icon={SlackIcon} className="bg-[#4A154B]" />
              <h2 className="text-3xl font-bold">Add Slack</h2>
              <span className="text-xs font-normal px-2 py-0.5 rounded-full border text-surface-foreground/60">
                Optional
              </span>
            </div>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Slack integration is optional. It lets your team get notified
              about new savings opportunities and manage recommendations without
              leaving Slack.
            </p>

            <h3 className="text-xl font-bold mb-3 mt-8">What you get</h3>
            <ul className="list-disc list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>
                <strong>Notifications</strong> when new recommendations appear.
              </li>
              <li>
                <strong>PR generation</strong> directly from Slack. Generate a
                Terraform PR from a notification without leaving the channel.
              </li>
              <li>
                <strong>Dismiss recommendations</strong> from Slack if a
                suggestion isn't relevant.
              </li>
              <li>
                <strong>Flexible routing</strong> so the right team sees the
                right recommendations. Each AWS account or GitHub repo can be
                assigned to a specific Slack channel.
              </li>
            </ul>

            <h3 className="text-xl font-bold mb-3 mt-8">Setup</h3>
            <ol className="list-decimal list-outside ml-5 space-y-2 text-surface-foreground/80 leading-relaxed mb-4">
              <li>Connect Slack from your workspace settings.</li>
              <li>
                For each AWS account or GitHub repo, assign a Slack channel to
                receive its notifications.
              </li>
            </ol>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              You can disconnect Slack at any time from your workspace settings.
            </p>
          </section>

          <hr className="border-border" />

          {/* Supported resources */}
          <section id="supported-resources" className="scroll-mt-24 py-12">
            <h2 className="text-3xl font-bold mb-4 mt-0">
              Supported resources
            </h2>
            <p className="text-surface-foreground/80 leading-relaxed mb-4">
              Recommendations are generated by deterministic analysis. No AI or
              machine learning is involved in deciding what to recommend. The
              analysis checks usage thresholds, data quality, and seasonality
              patterns for each resource.
            </p>
            <p className="text-surface-foreground/80 leading-relaxed mb-6">
              Each resource type has its own page covering the specific
              thresholds, methodology, and what changes in the generated
              Terraform PR.
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <Server className="size-3.5" />
                </div>
                <Link href="/products/ec2#thresholds" className="font-semibold text-primary hover:underline">
                  EC2 Instances
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <Layers className="size-3.5" />
                </div>
                <Link href="/products/auto-scaling-groups#thresholds" className="font-semibold text-primary hover:underline">
                  Auto Scaling Groups
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <Container className="size-3.5" />
                </div>
                <Link href="/products/ecs-fargate#thresholds" className="font-semibold text-primary hover:underline">
                  ECS Fargate
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-none">
                  <HardDrive className="size-3.5" />
                </div>
                <Link href="/products/ebs#thresholds" className="font-semibold text-primary hover:underline">
                  EBS Volumes
                </Link>
              </li>
            </ul>
            <p className="text-surface-foreground/80 leading-relaxed mb-2">
              Coming next:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-surface text-surface-foreground/60 flex items-center justify-center flex-none">
                  <Server className="size-3.5" />
                </div>
                <span className="font-semibold text-surface-foreground/70">
                  EC2 previous-gen migrations
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="size-7 rounded-full bg-surface text-surface-foreground/60 flex items-center justify-center flex-none">
                  <Cpu className="size-3.5" />
                </div>
                <span className="font-semibold text-surface-foreground/70">
                  EC2 Graviton migrations
                </span>
              </li>
            </ul>
          </section>

          <hr className="border-border" />

          <FAQ items={FAQ_ITEMS} title="Setup and Usage FAQ" sectionId="faq" headingClassName="text-3xl font-bold mb-8 mt-0" className="scroll-mt-24 py-12" />

          <p className="text-surface-foreground/60 text-sm">
            Can't find what you need? Visit our{" "}
            <Link href="/support" className="text-primary hover:underline">
              Support page
            </Link>
            .
          </p>
        </div>
      </div>
    </Layout>
  );
}
